import { create } from "zustand";

import { DEFAULT_SEARCH_MATCH_MODE } from "@/constants";
import type { DeadlineFilterStatus, ProjectStatus, SearchMatchMode } from "@/types";

type ProjectFilterDraft = {
  selectedAssignedProject: string;
  selectedAssignedProjectLabel: string;
  selectedSite: string;
  selectedSiteLabel: string;
  selectedAssets: string[];
  selectedAssetLabels: string[];
  selectedStandard: string;
  projectStatusFilters: ProjectStatus[];
  deadlineFilters: DeadlineFilterStatus[];
};

type ProjectFilterApplied = {
  project_code?: string;
  project_label?: string;
  site_id?: string;
  site_name?: string;
  asset_ids?: string[];
  asset_names?: string[];
  standard_acronym?: string;
  project_status?: ProjectStatus[];
  deadline_status?: DeadlineFilterStatus[];
};

const EMPTY_DRAFT: ProjectFilterDraft = {
  selectedAssignedProject: "",
  selectedAssignedProjectLabel: "",
  selectedSite: "",
  selectedSiteLabel: "",
  selectedAssets: [],
  selectedAssetLabels: [],
  selectedStandard: "",
  projectStatusFilters: [],
  deadlineFilters: [],
};

interface ProjectFilterStore {
  draft: ProjectFilterDraft;
  applied: ProjectFilterApplied;
  search: string;
  matchMode: SearchMatchMode;
  setSearch: (value: string) => void;
  setMatchMode: (value: SearchMatchMode) => void;
  setDraft: (fields: Partial<ProjectFilterDraft>) => void;
  applyFilters: () => void;
  removeAppliedFilter: (patch: Partial<ProjectFilterApplied>) => void;
  syncDraftFromApplied: () => void;
  reset: () => void;
}

export const useProjectFilterStore = create<ProjectFilterStore>((set, get) => ({
  draft: { ...EMPTY_DRAFT },
  applied: {},
  search: "",
  matchMode: DEFAULT_SEARCH_MATCH_MODE,

  setSearch: (value) => set({ search: value }),
  setMatchMode: (value) => set({ matchMode: value }),

  setDraft: (fields) => set((state) => ({ draft: { ...state.draft, ...fields } })),

  applyFilters: () => {
    const { draft } = get();
    set({
      applied: {
        project_code: draft.selectedAssignedProject || undefined,
        project_label: draft.selectedAssignedProjectLabel || undefined,
        site_id: draft.selectedSite || undefined,
        site_name: draft.selectedSiteLabel || undefined,
        asset_ids: draft.selectedAssets.length ? draft.selectedAssets : undefined,
        asset_names: draft.selectedAssetLabels.length ? draft.selectedAssetLabels : undefined,
        standard_acronym: draft.selectedStandard || undefined,
        project_status: draft.projectStatusFilters.length ? draft.projectStatusFilters : undefined,
        deadline_status: draft.deadlineFilters.length ? draft.deadlineFilters : undefined,
      },
    });
  },

  removeAppliedFilter: (patch) => {
    const { applied } = get();
    const nextApplied = { ...applied, ...patch };
    if (!nextApplied.project_code) nextApplied.project_label = undefined;
    if (!nextApplied.site_name) nextApplied.site_id = undefined;
    if ("asset_ids" in patch) {
      // Caller supplied explicit IDs (index-based removal) — use as-is
    } else if (nextApplied.asset_names !== undefined) {
      // Name-based reconciliation for callers that only know names
      const ids: string[] = [];
      const labels: string[] = [];
      for (const label of nextApplied.asset_names) {
        const idx = (applied.asset_names ?? []).indexOf(label);
        const id = idx !== -1 ? (applied.asset_ids ?? [])[idx] : undefined;
        if (id !== undefined) {
          ids.push(id);
          labels.push(label);
        }
      }
      nextApplied.asset_ids = ids;
      nextApplied.asset_names = labels;
    } else {
      nextApplied.asset_ids = undefined;
    }
    set({
      applied: nextApplied,
      draft: {
        selectedAssignedProject: nextApplied.project_code ?? "",
        selectedAssignedProjectLabel: nextApplied.project_code
          ? (nextApplied.project_label ?? "")
          : "",
        selectedSite: nextApplied.site_id ?? "",
        selectedSiteLabel: nextApplied.site_name ?? "",
        selectedAssets: nextApplied.asset_ids ?? [],
        selectedAssetLabels: nextApplied.asset_names ?? [],
        selectedStandard: nextApplied.standard_acronym ?? "",
        projectStatusFilters: nextApplied.project_status ?? [],
        deadlineFilters: nextApplied.deadline_status ?? [],
      },
    });
  },

  syncDraftFromApplied: () => {
    const { applied } = get();
    set({
      draft: {
        selectedAssignedProject: applied.project_code ?? "",
        selectedAssignedProjectLabel: applied.project_code ? (applied.project_label ?? "") : "",
        selectedSite: applied.site_id ?? "",
        selectedSiteLabel: applied.site_name ?? "",
        selectedAssets: applied.asset_ids ?? [],
        selectedAssetLabels: applied.asset_names ?? [],
        selectedStandard: applied.standard_acronym ?? "",
        projectStatusFilters: applied.project_status ?? [],
        deadlineFilters: applied.deadline_status ?? [],
      },
    });
  },

  reset: () =>
    set({
      draft: { ...EMPTY_DRAFT },
      applied: {},
      search: "",
      matchMode: DEFAULT_SEARCH_MATCH_MODE,
    }),
}));
